import { useState } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import ChatPanel from "@/components/ChatPanel";
import { LayoutDashboard, Briefcase, Users, Receipt, LogOut, MessageSquare, X, Settings, Wallet, Menu, PanelRightClose, PanelRightOpen } from "lucide-react";

const navGroups = [
  { titolo: "Operativo", voci: [
    { to: "/", label: "Home", icon: LayoutDashboard, end: true },
    { to: "/pratiche", label: "Pratiche", icon: Briefcase },
    { to: "/clienti", label: "Clienti", icon: Users },
  ]},
  { titolo: "Amministrazione", perm: "vedi_dati_economici", voci: [
    { to: "/fatture", label: "Fatture", icon: Receipt },
    { to: "/contabilita", label: "Contabilità", icon: Wallet },
  ]},
  { titolo: "Studio", voci: [
    { to: "/impostazioni", label: "Impostazioni", icon: Settings },
  ]},
];

function NavItems({ onNavigate }) {
  const { can } = useAuth();
  const { data: nuovi } = useQuery({
    queryKey: ["nuovi-documenti"],
    queryFn: async () => (await api.get("/dashboard/nuovi-documenti")).data,
    refetchInterval: 1000 * 60 * 5,
  });
  return (
    <>
      {navGroups.filter((g) => !g.perm || can(g.perm)).map((g, gi) => (
        <div key={g.titolo} className={gi === 0 ? "" : "pt-3.5 mt-3.5 border-t border-white/10"}>
          <p className="px-3 pb-2 text-[11px] font-bold uppercase tracking-[1.1px] text-white/40">{g.titolo}</p>
          {g.voci.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end} onClick={onNavigate} data-testid={`nav-${n.label.toLowerCase().replace(" ", "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-2.5 px-3 py-2.5 mb-0.5 text-[14px] rounded-[4px] transition-colors duration-150 ${
                  isActive ? "bg-white/[0.10] text-white font-bold shadow-[inset_3px_0_0_0_#A8863F]" : "text-white/65 hover:text-white hover:bg-white/[0.06]"
                }`
              }>
              <n.icon className="w-4 h-4" strokeWidth={1.6} />
              {n.label}
              {n.to === "/" && nuovi?.totale > 0 && (
                <span data-testid="nav-nuovi-doc-badge"
                  className="ml-auto text-[11px] font-bold bg-[#E7D9B2] text-[#1B2A4B] rounded-[3px] px-1.5 py-0.5">
                  {nuovi.totale}
                </span>
              )}
            </NavLink>
          ))}
        </div>
      ))}
    </>
  );
}

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [chatOpen, setChatOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [chatCollapsed, setChatCollapsed] = useState(() => localStorage.getItem("rb_chat_collapsed") === "1");
  const toggleChat = (v) => { localStorage.setItem("rb_chat_collapsed", v ? "1" : "0"); setChatCollapsed(v); };
  const doLogout = () => { logout(); navigate("/login"); };

  return (
    <div className="min-h-screen flex bg-[#F2F1EC]">
      {/* Sidebar (desktop) */}
      <aside className="hidden md:flex flex-col w-[220px] bg-[#1B2A4B] text-white shrink-0">
        <div className="px-4 py-3.5 border-b border-white/10 flex items-center gap-2.5">
          <img src="/rb-logo.webp" alt="RB Studio" className="w-8 h-8 object-contain shrink-0" />
          <div className="leading-none">
            <div className="text-[13px] font-bold tracking-[1.1px] text-white">RB STUDIO</div>
            <div className="text-[10px] font-semibold tracking-[1.6px] text-[#E7D9B2] mt-1">ASSISTANT</div>
          </div>
        </div>
        <nav className="flex-1 py-3 px-2 space-y-0.5">
          <NavItems />
        </nav>
        <div className="px-4 py-3.5 border-t border-white/10">
          <div className="text-[14px] font-bold">{user?.nome}</div>
          <div className="text-[12px] text-white/50 capitalize mb-2">{user?.ruolo}</div>
          <button data-testid="logout-btn" onClick={doLogout}
            className="flex items-center gap-2 text-[13px] font-semibold text-white/70 hover:text-[#E7D9B2] transition-colors">
            <LogOut className="w-3.5 h-3.5" /> Esci
          </button>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 min-w-0 overflow-y-auto max-h-screen">
        {/* Mobile header */}
        <header className="md:hidden sticky top-0 z-30 flex items-center justify-between bg-[#1B2A4B] text-white px-3 py-2">
          <div className="flex items-center gap-2.5">
            <img src="/rb-logo.webp" alt="RB Studio" className="w-9 h-9 object-contain shrink-0" />
            <div className="leading-none">
              <div className="text-[12px] font-bold tracking-[1.1px] text-white">RB STUDIO</div>
              <div className="text-[9px] font-semibold tracking-[1.6px] text-[#E7D9B2] mt-0.5">ASSISTANT</div>
            </div>
          </div>
          <button data-testid="mobile-menu-toggle" onClick={() => setMenuOpen(true)} aria-label="Apri menu"
            className="p-2 rounded-[4px] hover:bg-white/10 transition-colors">
            <Menu className="w-6 h-6" />
          </button>
        </header>
        <div className="p-4 sm:p-5 lg:p-7">
          <Outlet />
        </div>
      </main>

      {/* Mobile navigation drawer */}
      {menuOpen && (
        <div className="md:hidden fixed inset-0 z-[60] flex" data-testid="mobile-menu-drawer">
          <div className="absolute inset-0 bg-black/50" onClick={() => setMenuOpen(false)} />
          <aside className="relative w-64 max-w-[82%] h-full bg-[#1B2A4B] text-white flex flex-col animate-in slide-in-from-left duration-200">
            <div className="px-5 py-5 border-b border-white/10 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <img src="/rb-logo.webp" alt="RB Studio" className="w-10 h-10 object-contain shrink-0" />
                <div className="leading-none">
                  <div className="font-serif text-[12.5px] tracking-[0.16em] text-white">RB STUDIO</div>
                  <div className="text-[9px] tracking-[0.22em] text-[#C9A84C] mt-1">ASSISTANT</div>
                </div>
              </div>
              <button data-testid="mobile-menu-close" onClick={() => setMenuOpen(false)} aria-label="Chiudi menu" className="p-1.5 rounded-lg hover:bg-white/10">
                <X className="w-5 h-5" />
              </button>
            </div>
            <nav className="flex-1 py-3 px-2 space-y-0.5 overflow-y-auto">
              <NavItems onNavigate={() => setMenuOpen(false)} />
            </nav>
            <div className="px-6 py-5 border-t border-white/10">
              <div className="text-sm">{user?.nome}</div>
              <div className="text-[11px] text-white/50 capitalize mb-3">{user?.ruolo}</div>
              <button data-testid="mobile-logout-btn" onClick={doLogout}
                className="flex items-center gap-2 text-xs text-white/70 hover:text-[#C9A84C] transition-colors">
                <LogOut className="w-3.5 h-3.5" /> Esci
              </button>
            </div>
          </aside>
        </div>
      )}

      {/* Chat panel (desktop) — collassabile */}
      {!chatCollapsed ? (
        <div className="hidden xl:block w-[340px] shrink-0 border-l border-[#E2E0D9] bg-white max-h-screen relative">
          <button data-testid="chat-collapse-btn" onClick={() => toggleChat(true)} title="Nascondi Vera"
            className="absolute top-3.5 right-3 z-10 p-1.5 rounded-[4px] text-[#5A5A5A] hover:text-[#1B2A4B] hover:bg-[#F7F6F2] transition-colors">
            <PanelRightClose className="w-5 h-5" />
          </button>
          <ChatPanel />
        </div>
      ) : (
        <button data-testid="chat-expand-btn" onClick={() => toggleChat(false)} title="Mostra Vera"
          className="hidden xl:flex fixed right-0 top-24 z-40 items-center gap-2 bg-[#1B2A4B] text-white pl-3 pr-4 py-2.5 rounded-l-[4px] shadow-lg hover:bg-[#24375f] transition-colors">
          <PanelRightOpen className="w-5 h-5 text-[#E7D9B2]" />
          <span className="text-[14px] font-bold">Vera</span>
        </button>
      )}

      {/* Mobile/tablet chat toggle */}
      <button data-testid="chat-toggle" onClick={() => setChatOpen(true)}
        className="xl:hidden fixed bottom-5 right-5 z-40 bg-[#1B2A4B] text-white w-14 h-14 rounded-full flex items-center justify-center shadow-lg hover:scale-105 transition-transform">
        <MessageSquare className="w-6 h-6" />
      </button>
      {chatOpen && (
        <div className="xl:hidden fixed inset-0 z-50 bg-white flex flex-col">
          <div className="flex justify-end p-3 border-b border-[#1A2B4A]/10">
            <button data-testid="chat-close" onClick={() => setChatOpen(false)}><X className="w-6 h-6 text-[#1A2B4A]" /></button>
          </div>
          <div className="flex-1 min-h-0"><ChatPanel /></div>
        </div>
      )}
    </div>
  );
}
